import React from 'react';

// TopupContext owns the "add credits" modal's open/close state so any
// component can trigger it — Header credit-balance chip, Profile page's
// "Add credits" button, future in-flight quota-warning banners, etc.
//
// The <TopupDialog> itself is mounted once at the app root (App.tsx) so
// it renders above every route without duplicating state.
//
// `canTopup` used to gate the UI to admin-only during the Stripe wire-up.
// Kept as a constant true after GA so call sites don't need to change
// when the flag returns (e.g. per-tier gating). Remove callers if this
// stays constant long-term.

interface TopupContextValue {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  /** Always true post-GA. Reserved for future gating (per-plan, kill-
   *  switch during an outage, etc.); call sites should keep the check. */
  canTopup: boolean;
}

const Ctx = React.createContext<TopupContextValue | null>(null);

export const TopupProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = React.useState(false);

  const value = React.useMemo<TopupContextValue>(() => ({
    isOpen,
    canTopup: true,
    open: () => setIsOpen(true),
    close: () => setIsOpen(false),
  }), [isOpen]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const useTopup = (): TopupContextValue => {
  const v = React.useContext(Ctx);
  if (!v) throw new Error('useTopup must be used within <TopupProvider>');
  return v;
};
